import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { FileUp, FolderOpen, Library, Plus } from "lucide-react";

import logo from "@/assets/logo.svg";
import { AccountMenu } from "@/auth/AccountMenu";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { subscribeDeckMessages } from "@/lib/deckChannel";
import { listDecks, type DeckIndexEntry } from "@/lib/idb";
import { useDesktopActions } from "@/desktop/useDesktopActions";

const RECENT_COUNT = 8;

/**
 * §14.7.3 What the desktop app shows when nothing is open.
 *
 * On the web, `/` is a decision that always lands you in a deck (`DeckLanding`) — a browser
 * tab that opens on a chooser feels broken. A desktop app is the other way round: it launches
 * with no document, and inventing one on every launch would litter the user's list with empty
 * decks. So the shell gets a real start screen, and `DeckLanding` keeps the web behaviour.
 */
export function StartScreen() {
  const navigate = useNavigate();
  const { newDeck, newFromLibrary, openDeckFile } = useDesktopActions();
  const [decks, setDecks] = useState<DeckIndexEntry[] | null>(null);

  const refresh = useCallback(async () => {
    // Already sorted by `updatedAt` — the §12.3 index exists so this costs one small read
    // rather than deserializing every slide in the database.
    setDecks((await listDecks()).slice(0, RECENT_COUNT));
  }, []);

  useEffect(() => {
    void refresh();
    return subscribeDeckMessages(() => void refresh());
  }, [refresh]);

  return (
    <div className="app-screen-min flex flex-col bg-slate-100">
      <header className="flex items-center gap-6 bg-slate-900 px-6 py-3 text-white">
        <img src={logo} alt="DeckAI" className="h-8 w-auto" />
        <span className="flex-1 text-sm font-medium">SlideCraft Studio</span>
        <AccountMenu />
      </header>

      <main className="flex-1 px-8 py-10">
        <div className="mx-auto max-w-4xl">
          <h1 className="text-xl font-semibold text-slate-900">
            Start a document
          </h1>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StartAction
              icon={<Plus className="h-5 w-5" />}
              label="New blank document"
              onClick={() => void newDeck()}
            />
            <StartAction
              icon={<Library className="h-5 w-5" />}
              label="New from the Gridd Library"
              onClick={() => void newFromLibrary()}
            />
            <StartAction
              icon={<FileUp className="h-5 w-5" />}
              label="Open file…"
              onClick={() => void openDeckFile()}
            />
          </div>

          <div className="mt-10 flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-900">
              Recent documents
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/documents")}
            >
              <FolderOpen className="mr-2 h-4 w-4" /> All documents
            </Button>
          </div>

          {decks === null ? (
            <div className="mt-4 space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-12 rounded-md" />
              ))}
            </div>
          ) : decks.length === 0 ? (
            <p className="mt-4 rounded-lg border border-dashed border-slate-300 bg-white px-6 py-10 text-center text-sm text-slate-600">
              Nothing here yet. Start a blank document, or open a{" "}
              <code className="rounded bg-slate-100 px-1">.deckai</code> file
              you saved earlier.
            </p>
          ) : (
            <ul className="mt-4 divide-y divide-slate-200 overflow-hidden rounded-lg border border-slate-200 bg-white">
              {decks.map((entry) => (
                <li key={entry.deckId}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-4 px-4 py-3 text-left hover:bg-slate-50"
                    onClick={() => navigate(`/d/${entry.deckId}`)}
                  >
                    <span className="h-10 w-16 shrink-0 overflow-hidden rounded border border-slate-200 bg-slate-50">
                      {entry.thumbDataUrl && (
                        <img
                          src={entry.thumbDataUrl}
                          alt=""
                          className="h-full w-full object-contain"
                        />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-900">
                        {entry.title || "Untitled deck"}
                      </span>
                      <span className="block truncate text-xs text-slate-500">
                        {relativeTime(entry.updatedAt)} · {entry.slideCount}{" "}
                        {entry.slideCount === 1 ? "slide" : "slides"}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* §14.7.5 The absent Save button is the single most alarming thing about this app
              for anyone who has ever lost work to one. Say it plainly, once, where they land. */}
          <p className="mt-8 text-xs text-slate-500">
            Your documents save automatically as you work. “Save a copy” (⌘⇧S)
            writes a{" "}
            <code className="rounded bg-slate-200/70 px-1">.deckai</code> file
            you can move or back up.
          </p>
        </div>
      </main>
    </div>
  );
}

function StartAction({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-4 text-left text-sm font-medium text-slate-800 shadow-sm transition-shadow hover:shadow-md"
    >
      <span className="text-slate-500">{icon}</span>
      {label}
    </button>
  );
}

// A stored timestamp can be malformed (hand-edited storage, a truncated import), and a row is
// not worth throwing a render for.
function relativeTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Updated recently";
  return `Updated ${formatDistanceToNow(date, { addSuffix: true })}`;
}
